
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';
import { getCurrentUser, isAuthenticated, logoutUser } from '@/utils/auth';
import { getUserPoints, getUserReferralCode } from '@/utils/referral';
import ProfileHeader from '@/components/profile/ProfileHeader';
import UserCard from '@/components/profile/UserCard';
import ReferralLinkCard from '@/components/profile/ReferralLinkCard';
import ProfileForm from '@/components/profile/ProfileForm';
import PaymentReceivingForm from '@/components/profile/PaymentReceivingForm';
import LoadingState from '@/components/profile/LoadingState';
import { Button } from '@/components/ui/button';
import { MessageSquare, User, CreditCard } from 'lucide-react';
import SupportDialog from '@/components/SupportDialog';
import TaskNotification from '@/components/profile/TaskNotification';
import { useTaskNotifications } from '@/hooks/useTaskNotifications';
import BackToDashboardButton from '@/components/navigation/BackToDashboardButton';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [points, setPoints] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const businessDomain = 'yourbusiness.com';
  
  const { tasks, markTaskComplete } = useTaskNotifications(user);
  
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    
    const currentUser = getCurrentUser();
    setUser(currentUser);
    
    const loadData = async () => {
      try {
        const [pointsData, referralCodeData] = await Promise.all([
          getUserPoints(currentUser.id),
          getUserReferralCode(currentUser.id)
        ]);
        
        setPoints(pointsData);
        setReferralCode(referralCodeData);
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [navigate]);
  
  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  const handleProfileUpdate = async (updatedData: any) => {
    const updatedUser = {
      ...user,
      name: updatedData.name,
      email: updatedData.email,
      address: updatedData.address,
      city: updatedData.city,
      state: updatedData.state,
      zip: updatedData.zip,
      country: updatedData.country,
      phone: updatedData.phone,
      referralTemplate: updatedData.referralTemplate,
    };
    
    setUser(updatedUser);
    
    if (updatedData.name && updatedData.email && updatedData.address &&
        updatedData.city && updatedData.state && updatedData.zip &&
        updatedData.country && updatedData.phone) {
      markTaskComplete('profile');
    }
    
    return Promise.resolve();
  };

  const handlePaymentDetailsSave = async (paymentData: any) => {
    const updatedUser = {
      ...user,
      ...paymentData,
      preferredPaymentMethod: paymentData.preferredMethod
    };
    
    setUser(updatedUser);
    markTaskComplete('payment');
    
    console.log('Payment details to save:', paymentData);
    return Promise.resolve();
  };
  
  if (isLoading) {
    return <LoadingState onLogout={handleLogout} />;
  }
  
  const referralLink = user?.referralLink || `https://${businessDomain}/?ref=${referralCode}`;
  
  const tasksWithIcons = tasks.map(task => ({
    ...task,
    icon: task.id === 'profile' ? <User size={16} /> : <CreditCard size={16} />
  }));
  
  return (
    <div className="min-h-screen flex flex-col">
      <ProfileHeader isAdmin={user?.isAdmin} onLogout={handleLogout} />
      
      <main className="flex-grow py-8 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackToDashboardButton />
          
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Your Profile</h1>
              <p className="text-gray-600">Manage your account and referral preferences</p>
            </div>
            <div className="flex items-center gap-2">
              <TaskNotification 
                tasks={tasksWithIcons} 
                onTaskComplete={markTaskComplete} 
              />
              <Button 
                onClick={() => setSupportDialogOpen(true)}
                variant="outline" 
                className="flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                <span>Support Center</span>
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <UserCard user={user} points={points} />
              <ReferralLinkCard 
                referralCode={referralCode}
                referralLink={referralLink}
                userName={user.name}
                userId={user.id || "user123"}
              />
            </div>
            
            <div className="lg:col-span-2 space-y-8">
              <ProfileForm 
                user={user} 
                onProfileUpdate={handleProfileUpdate}
              />
              
              <PaymentReceivingForm 
                onSave={handlePaymentDetailsSave}
                initialData={{
                  accountHolderName: user.name || '',
                  paypalEmail: user.email || '',
                  preferredMethod: user.preferredPaymentMethod || 'bank'
                }}
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
      
      <SupportDialog 
        open={supportDialogOpen} 
        onOpenChange={setSupportDialogOpen}
        defaultValues={{
          name: user?.name,
          email: user?.email
        }}
      />
    </div>
  );
};

export default Profile;
